// Imports
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../shareables/foundation/front-end/redux/hooks';
import GlobalStyle from '../shareables/foundation/simple-ui/components/global-style';
import apiActions from '../shareables/integrations/api/redux/actions';
import { GetSelfResponse } from '../shareables/types/api/main/self/get';
import WhiteBackground from './resources/white-background';
import * as Sentry from '@sentry/browser';
import selfActions from '../redux/self/actions';
import { GetWebsiteResponse } from '../shareables/types/api/main/website/get';
import TemplateStyle from './templates/template-style';
import PageMessages from '../shareables/foundation/front-end/components/resources/page-messages';
import commonActions from '../shareables/foundation/front-end/redux/actions';
import styled from 'styled-components/macro';
import Banner from './resources/banner';
import TopBar from './resources/top-bar';
import Footer from './resources/footer';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
	createBrowserRouter,
	createMemoryRouter,
	createRoutesFromElements,
	Outlet,
	Route,
	RouterProvider,
} from 'react-router-dom';
import ScrollManager from './resources/scroll-manager';
import appRoutes from './app-routes';
import { GetPagesResponse } from '../shareables/types/api/main/website/pages/get';
import { GetMemberResponse } from '../shareables/types/api/main/website/member/get';
import AdminBar from './resources/admin-bar';
import TroubleReachingBackend from './resources/error-boundary/trouble-reaching-back-end';
import RouteErrorBoundary from './resources/error-boundary/route-error-boundary';
import Matomo from './resources/scripts/matomo';
import GoogleAnalytics from './resources/scripts/google-analytics';
import { GetWebsitesParameters, GetWebsitesResponse } from '../shareables/types/api/main/self/websites/get';
import AppContext from './context';
import SkipToMainContent from './resources/skip-to-main-content';
import NonMemberNotice from './resources/non-member-notice';
import UpscopeLoader from './resources/scripts/upscope-loader';
import keyValueStorage from '../shareables/foundation/front-end/utils/key-value-storage';
import useMemberCheck from '../utils/use-member-check';


// Styled component
export const AppContainer = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 100vh;
`;

export const FluidContainer = styled.div`
	padding-right: 0.9375rem;
	padding-left: 0.9375rem;
	margin-right: auto;
	margin-left: auto;
	width: 100%;
	transition: all 0.5s;
	flex: 1 0 auto;
`;


// Define the accepted props
interface Props {
	storybookKeyword?: string;
}


// Function component
const App: React.FC<Props> = ({ storybookKeyword }) => {
	// Use state
	const [getWebsiteResponseError, setGetWebsiteResponseError] = useState(false);
	
	
	// Use ref
	const appContainerRef = useRef<HTMLDivElement>(null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const signedIn = useAppSelector((state) => state.common.user.signedIn);
	const website = useAppSelector((state) => state.self.website);
	const member = useAppSelector((state) => state.self.member);
	const memberLoaded = useAppSelector((state) => state.self.memberLoaded);
	const adminView = useAppSelector((state) => state.self.adminView);
	const globalSignInLoaded = useAppSelector((state) => state.self.globalSignInLoaded);
	const isAdmin = useMemberCheck({ type: 'ANY_PRIVILEGE' });
	const isMember = useMemberCheck({ type: 'MEMBER' });
	
	
	// Request website details
	useEffect(() => {
		// Call API
		void dispatch(
			apiActions.call<undefined, undefined, GetWebsiteResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}`,
				cancelOnRouteChange: false,
				handleErrorsAutomatically: false,
				completion: (json) => {
					// Check for response errors
					if (json.status === 'error') {
						// Handle expected error message
						if (json.message.toLowerCase().includes('website not found.')) {
							window.location.href = `https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/website-not-found`;
							return;
						}
						
						
						// Handle all other errors
						setGetWebsiteResponseError(true);
					} else {
						// Handle response success
						dispatch(selfActions.storeWebsite(json.response));
					}
				},
			})
		);
		
		
		// Call API
		void dispatch(
			apiActions.call<undefined, undefined, GetPagesResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/pages`,
				completion: (json) => {
					dispatch(selfActions.storePages(json.response));
				},
			})
		);
	}, [dispatch]);
	
	
	// Check if user is signed in and retrieve details about them
	useEffect(() => {
		// Handle based on whether the user is signed in
		if (!signedIn) {
			// Call API
			void dispatch(
				apiActions.call<undefined, undefined, GetSelfResponse>({
					action: 'GET',
					uri: '/self',
					cancelOnRouteChange: false,
					completion: (json) => {
						// Store user for Sentry
						Sentry.configureScope((scope) => {
							scope.setUser({
								id: String(json.response.id),
								email: json.response.email,
							});
						});
						
						
						// Store response in Redux
						dispatch(commonActions.signedIn(json.response));
						dispatch(selfActions.setPhone(json.response.intercom.phone ?? null));
					},
					disableRedirectToSignIn: true,
				})
			);
		}
	}, [signedIn, dispatch]);
	
	
	// Handle website redirects
	useEffect(() => {
		// Check website status
		if (website?.inactiveBecause) {
			// Map inactive reasons to redirect paths
			const redirectMap: Record<Exclude<GetWebsiteResponse['inactiveBecause'], undefined>, string> = {
				DMCA_TAKEN_DOWN: 'website-taken-down',
				ADMIN_DISABLED: 'website-disabled',
				PENDING_DELETION: 'website-deleted',
			};
			
			
			// Redirect based on the reason
			window.location.href = `https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/${
				redirectMap[website.inactiveBecause]
			}`;
			
			
			// Return
			return;
		}
		
		
		// Check website status
		if (website?.inStartProcess) {
			// Redirect to start front-end
			window.location.href = `https://start.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}`;
			
			
			// Return
			return;
		}
		
		
		// Check for website redirect
		if (website?.redirect) {
			// Redirect
			window.location.href = `${website.redirect}${window.location.pathname}${window.location.search}${window.location.hash}`;
		}
	}, [website]);
	
	
	// Request member details
	useEffect(() => {
		// Call API
		void dispatch(
			apiActions.call<undefined, undefined, GetMemberResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/member`,
				handleErrorsAutomatically: false,
				completion: (json) => {
					// Update state
					dispatch(selfActions.setMemberLoaded());
					
					
					// Check for response errors
					if (json.status === 'error') {
						// Handle expected error messages
						if (
							json.message.toLowerCase().includes('you are not a member of this website.') ||
							json.message.toLowerCase().includes('website is inactive.')
						) {
							// Do nothing
							return;
						}
						
						
						// Handle all other errors
						dispatch(
							commonActions.showPageMessage({
								color: 'danger',
								title: 'Oh snap!',
								message: json.message,
							})
						);
						
						
						// Return
						return;
					}
					
					
					// Handle response success
					dispatch(selfActions.storeMember(json.response));
				},
				disableRedirectToSignIn: true,
			})
		);
	}, [dispatch]);
	
	
	// Request global sign in status, if necessary
	useEffect(() => {
		// Return early
		if (!website?.id || !signedIn) {
			return;
		}
		
		if (member) {
			dispatch(selfActions.setGlobalSignInLoaded());
			return;
		}
		
		
		// Call API
		void dispatch(
			apiActions.call<GetWebsitesParameters, undefined, GetWebsitesResponse>({
				action: 'GET',
				uri: '/self/websites',
				cancelOnRouteChange: false,
				completion: (json) => {
					// Check for relevant property
					if (json.response[0] && 'globalSignIn' in json.response[0] && json.response[0].globalSignIn) {
						// Update state in Redux
						dispatch(selfActions.setGlobalSignIn(true));
					}
					
					
					// Update state
					dispatch(selfActions.setGlobalSignInLoaded());
				},
				parameters: {
					websiteID: website.id,
				},
				disableRedirectToSignIn: true,
			})
		);
	}, [dispatch, website?.id, signedIn, member]);
	
	
	// Update state if admin wishes to view the site as non-admin
	useEffect(() => {
		if (isAdmin && keyValueStorage.getItem('viewing_as_non_admin')) {
			dispatch(selfActions.setAdminView(false));
		}
	}, [dispatch, isAdmin]);
	
	
	// Render nothing when redirecting
	if (website?.inactiveBecause || website?.inStartProcess || website?.redirect) {
		return null;
	}
	
	
	// Handle website response errors
	if (getWebsiteResponseError) {
		return (
			<HelmetProvider>
				<TroubleReachingBackend />
			</HelmetProvider>
		);
	}
	
	
	// Build inner content
	const innerContent = (
		<React.Fragment>
			<ScrollManager />
			{!(window as typeof window & { STORYBOOK?: true }).STORYBOOK && <PageMessages />}
			
			<Helmet>{website?.customCSS && <style>{website.customCSS}</style>}</Helmet>
			
			{website && (
				<AppContext.Provider value={{ appContainerRef }}>
					<AppContainer ref={appContainerRef}>
						<TemplateStyle id={website.templateID}>
							<SkipToMainContent />
							
							{isAdmin && adminView && <AdminBar />}
							
							<TopBar />
							
							{signedIn && !isMember && memberLoaded && globalSignInLoaded && <NonMemberNotice />}
							
							<FluidContainer>
								<Banner />
								<Outlet />
							</FluidContainer>
							
							<Footer storybookKeyword={storybookKeyword} />
						</TemplateStyle>
						
						<Matomo />
						<GoogleAnalytics />
						<UpscopeLoader />
					</AppContainer>
				</AppContext.Provider>
			)}
		</React.Fragment>
	);
	
	
	// Use React Router functionality
	const routes = createRoutesFromElements(
		<Route element={innerContent} errorElement={<RouteErrorBoundary />}>
			{appRoutes}
		</Route>
	);
	
	const router = (window as typeof window & { STORYBOOK?: boolean }).STORYBOOK
		? createMemoryRouter(routes)
		: createBrowserRouter(routes);
	
	
	// Return JSX
	return (
		<React.StrictMode>
			<HelmetProvider>
				<GlobalStyle />
				<WhiteBackground />
				
				<RouterProvider router={router} />
			</HelmetProvider>
		</React.StrictMode>
	);
};

export default App;
